import { Role, rolesManipulator } from 'auth';
import classNames from 'classnames';
import { FormikProps, FormikProvider } from 'formik';
import { ReactNode, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  hasFormikErrors,
  scrollToFormikError,
} from '@/modules/entity/utils/scrollToFormikError.ts';
import { Button, Tooltip, TooltipNew } from 'ui';

export interface OnboardingHolderWrapperProps {
  title?: string;
  titleHint?: string;
  children: ReactNode;
  onClickContinue?: () => void;
  onClickSkip?: () => void;
  buttonSkipName?: string;
  buttonContinueHint?: string | ReactNode;
  buttonContinueName?: string;
  messageInsteadButtons?: string | ReactNode;
  formik?: FormikProps<any>;
  isLoading?: boolean;
  disableNext?: boolean;
  className?: string;
  isConfirmPage?: boolean;
  showBackButton?: boolean;
}

export const OnboardingHolderWrapper = ({
  title,
  children,
  onClickContinue,
  formik,
  isLoading,
  disableNext,
  onClickSkip,
  className,
  buttonSkipName = 'Skip',
  buttonContinueName = 'Continue',
  buttonContinueHint,
  titleHint,
  messageInsteadButtons,
  isConfirmPage = false,
  showBackButton = true,
}: OnboardingHolderWrapperProps) => {
  const { state } = useLocation();
  const [params, setParams] = useSearchParams();
  const isIssuerAdmin = rolesManipulator.current_user_role === Role.SR_ISSUER_ADMIN;
  const isUpdate = state?.action === 'update';
  const notifiedSubmitCountRef = useRef(0);
  const formErrors = formik?.errors;
  const isValidating = formik?.isValidating ?? false;
  const submitCount = formik?.submitCount ?? 0;

  useEffect(() => {
    if (submitCount < notifiedSubmitCountRef.current) {
      notifiedSubmitCountRef.current = 0;
    }

    if (isValidating || submitCount === notifiedSubmitCountRef.current) return;

    if (!formErrors || !hasFormikErrors(formErrors)) return;

    notifiedSubmitCountRef.current = submitCount;
    scrollToFormikError(formErrors);
    toast.error('Please correct the highlighted fields before continuing.', {
      id: 'onboarding-validation-error',
    });
  }, [formErrors, isValidating, submitCount]);

  const currentStep = Number(params.get('step') ?? 0);
  const currentSubStep = Number(params.get(`sub_step${currentStep}`) ?? 0);
  const currentSubStepRoad = Number(
    params.get(`sub_step_road${currentStep}_${currentSubStep}`) ?? 0,
  );
  const canGoBack = currentStep > 0 || currentSubStep > 0 || currentSubStepRoad > 0;

  const handleBack = () => {
    if (currentSubStepRoad > 0) {
      params.set(`sub_step_road${currentStep}_${currentSubStep}`, String(currentSubStepRoad - 1));
    } else if (currentSubStep > 0) {
      params.set(`sub_step${currentStep}`, String(currentSubStep - 1));
      params.delete(`sub_step_road${currentStep}_${currentSubStep}`);
    } else if (currentStep > 0) {
      params.set('step', String(currentStep - 1));
      params.delete(`sub_step_road${currentStep}_${currentSubStep}`);
      params.delete(`sub_step${currentStep}`);
    } else {
      return;
    }

    setParams(params, { state, replace: false });
  };

  const hintSaveButtonToRequest = useMemo<string>(() => {
    if (!isUpdate || isIssuerAdmin) return '';
    return `Please note! In order for this information to change on the holder's page, ${
      !isConfirmPage ? 'it must be confirmed on the confirmation tab and' : ''
    } for the Issuer admin to approve it on the Holders page in the "Edit requests" tab.`;
  }, [isConfirmPage, isIssuerAdmin, isUpdate]);

  const Body = (
    <div
      className={classNames(
        'flex flex-col space-y-10 min-h-[500px]',
        {
          'w-full': !className?.includes('w-'),
        },
        className,
      )}
    >
      {title && (
        <TooltipNew text={titleHint || ''} disabled={!titleHint}>
          <span
            style={{ wordBreak: 'break-word' }}
            className={classNames('text-[18px] leading-6 font-[500] font-[Inter] break-words', {
              'cursor-help': !!titleHint,
            })}
          >
            {title}
          </span>
        </TooltipNew>
      )}
      {children}
      <div className="w-full flex flex-col items-center justify-between gap-4">
        {messageInsteadButtons ? (
          <span className="w-full text-left text-xl text-blue-700">{messageInsteadButtons}</span>
        ) : (
          <>
            {onClickContinue && (
              <Tooltip
                position="up"
                positionHorizontal="center"
                variant="gray"
                classNameTooltip="border"
                className="w-full"
                button={
                  <TooltipNew
                    text={hintSaveButtonToRequest}
                    disabled={!hintSaveButtonToRequest || !!buttonContinueHint}
                    offset={10}
                    className="w-full"
                  >
                    <Button
                      className="w-full relative"
                      type="submit"
                      onClick={onClickContinue}
                      disabled={isLoading || disableNext}
                    >
                      {isUpdate && !isConfirmPage ? 'Save change' : buttonContinueName}
                    </Button>
                  </TooltipNew>
                }
                disabled={!buttonContinueHint}
              >
                <span>{buttonContinueHint}</span>
              </Tooltip>
            )}
            {onClickSkip && (
              <Button className="w-full" variant="secondary" type="submit" onClick={onClickSkip}>
                {buttonSkipName}
              </Button>
            )}
            {canGoBack && showBackButton && (
              <Button className="w-full" variant="secondary" type="button" onClick={handleBack}>
                Back
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );

  if (formik) {
    return <FormikProvider value={formik}>{Body}</FormikProvider>;
  }

  return <>{Body}</>;
};
